import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaBus, FaCheckCircle, FaExclamationTriangle, FaTachometerAlt, FaChartLine } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../styles/Flota.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const FlotaResumen = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    cargarResumen();
  }, []);

  const cargarResumen = async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      if (!authToken || !user) {
        setError('No hay sesión activa');
        return;
      }

      const userId = user.Legajo || user.CdEmpleado || user.CdUsuario;
      const userRole = user.IdRol || user.tipoUsuario || 1;

      const response = await axios.get(`${API_URL}/api/flota/resumen`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'x-user-id': String(userId),
          'x-user-role': String(userRole)
        }
      });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('No se pudo cargar el resumen');
      }
    } catch (err) {
      console.error('Error al cargar resumen:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-AR').format(Math.round(num));
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando resumen de flota...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <div className="flota-resumen">
      <div className="page-header mb-4">
        <h2><FaBus className="me-2" />Resumen General de Flota</h2>
        <p className="text-muted">Vista general del estado y actividad de las unidades</p>
      </div>

      {/* Cards de indicadores principales */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-icon bg-primary">
                <FaBus />
              </div>
              <div className="stat-content">
                <h3>{data.totales.internos}</h3>
                <p>Total Unidades</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-icon bg-success">
                <FaCheckCircle />
              </div>
              <div className="stat-content">
                <h3>{data.totales.activosHoy}</h3>
                <p>Activos Hoy</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-icon bg-warning">
                <FaExclamationTriangle />
              </div>
              <div className="stat-content">
                <h3>{data.totales.inactivosUltimaSemana}</h3>
                <p>Sin Actividad (7d)</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-icon bg-danger">
                <FaExclamationTriangle />
              </div>
              <div className="stat-content">
                <h3>{data.totales.conNovedades}</h3>
                <p>Con Novedades</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Kilómetros */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center mb-2">
                <FaTachometerAlt className="text-primary me-2" size={24} />
                <h5 className="mb-0">Kilómetros del Día</h5>
              </div>
              <h2 className="text-primary mb-0">{formatNumber(data.kilometros.hoy)} km</h2>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-3">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center mb-2">
                <FaChartLine className="text-info me-2" size={24} />
                <h5 className="mb-0">Kilómetros del Mes</h5>
              </div>
              <h2 className="text-info mb-0">{formatNumber(data.kilometros.mes)} km</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Distribución por Estado */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Distribución por Estado</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th className="text-center">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {data.distribucion.porEstado.map((item) => (
                    <tr key={item.CdEstado}>
                      <td>
                        <Badge 
                          bg="secondary" 
                          style={{ backgroundColor: item.Color || '#6c757d' }}
                        >
                          {item.NmEstado}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <strong>{item.cantidad}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Distribución por Base */}
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Distribución por Base</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Base</th>
                    <th className="text-center">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {data.distribucion.porBase.slice(0, 10).map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.NmBase || 'Sin base'}</td>
                      <td className="text-center">
                        <strong>{item.cantidad}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Rankings */}
      <Row>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">Top 5 - Más Activos (30 días)</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Interno</th>
                    <th>Dominio</th>
                    <th className="text-center">Días Activos</th>
                    <th className="text-end">Total Km</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rankings.masActivos.map((item) => (
                    <tr 
                      key={item.Interno}
                      onClick={() => navigate(`/administrador/flota/unidades/${item.Interno}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><strong>{item.Interno}</strong></td>
                      <td>{item.Dominio}</td>
                      <td className="text-center">
                        <Badge bg="success">{item.DiasActivos}</Badge>
                      </td>
                      <td className="text-end">{formatNumber(item.TotalKms)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-3">
          <Card>
            <Card.Header className="bg-warning">
              <h5 className="mb-0">Top 5 - Menos Activos (30 días)</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Interno</th>
                    <th>Dominio</th>
                    <th className="text-center">Días Activos</th>
                    <th className="text-end">Total Km</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rankings.menosActivos.map((item) => (
                    <tr 
                      key={item.Interno}
                      onClick={() => navigate(`/administrador/flota/unidades/${item.Interno}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><strong>{item.Interno}</strong></td>
                      <td>{item.Dominio}</td>
                      <td className="text-center">
                        <Badge bg="warning">{item.DiasActivos}</Badge>
                      </td>
                      <td className="text-end">{formatNumber(item.TotalKms)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FlotaResumen;
